import { Box, CircularProgress } from "@mui/material"
import ScanIcon from "assets/images/scan-bold.svg"
import { useCallback, useEffect, useRef, useState } from "react"
import Webcam from "react-webcam"
import { toast } from "react-toastify"
import styled from "styled-components"
import jsQR from "jsqr"

const position = "initial !important"

interface IContainerCam {
  border: boolean
  loading: boolean | undefined
}

const ContainerCam = styled.div<IContainerCam>`
  width: 70%;
  max-width: 300px;
  height: 100%;
  display: flex;
  justify-content: center;
  position: relative;
  z-index: 1;
  margin: 0 auto;

  & > img {
    position: absolute;
    width: 100%;
    height: 80%;
    padding: 1.5rem;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;
    filter: invert(100%) sepia(1%) saturate(7388%) hue-rotate(162deg) brightness(121%)
      contrast(100%);
  }

  & video {
    max-width: 100%;
    width: 100%;
    border-radius: 10px;
    height: auto !important;
    position: ${position};
    margin: 0 auto;
    border: ${props => (props.border ? "10px solid #89bd23" : "none")};
    opacity: ${props => (props.loading ? 0.5 : 1)};
  }

  &::before {
    content: "";
    position: absolute;
    width: calc(100% - 2rem);
    height: 6px;
    border-radius: 4px;
    background: #fff;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 5;
    display: ${props => (props.loading ? "none" : "block")};
  }
`

const LoadingOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 4;
  background: rgba(233, 229, 229, 0);
`

type TValue = "data_matrix" | "qr_code" | "bar_code"

const Cam = ({
  onSubmit,
  loading: externalLoading,
}: {
  onSubmit: (param: string) => void
  loading?: boolean
}) => {
  const [border, setBorder] = useState(false)
  const [isScannerActive, setIsScannerActive] = useState(true)
  const webcamRef = useRef<Webcam>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    setIsScannerActive(!externalLoading)
  }, [externalLoading])

  const capture = useCallback(() => {
    if (!webcamRef.current || !canvasRef.current || externalLoading || !isScannerActive) return

    const imageSrc = webcamRef.current.getScreenshot()
    if (!imageSrc) return

    const image = new Image()
    image.src = imageSrc
    image.onload = () => {
      const canvas = canvasRef.current
      if (!canvas) return

      canvas.width = image.width
      canvas.height = image.height
      const ctx = canvas.getContext("2d")
      if (!ctx) return

      ctx.drawImage(image, 0, 0)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const code = jsQR(imageData.data, imageData.width, imageData.height)

      if (code) {
        onSubmit(code.data)
        setBorder(true)
        navigator.vibrate(200)
        setTimeout(() => {
          setBorder(false)
        }, 1000)
      }
    }
  }, [externalLoading, isScannerActive, onSubmit])

  useEffect(() => {
    const interval = setInterval(capture, 500)
    return () => clearInterval(interval)
  }, [capture])

  const onError = () => {
    toast.error("Erro ao ativar a camera do dispositivo")
  }

  return (
    <ContainerCam border={border} loading={externalLoading}>
      <img src={ScanIcon} />
      {externalLoading ? (
        <LoadingOverlay>
          <CircularProgress style={{ color: "#89bd23", width: "40px", height: "40px" }} />
        </LoadingOverlay>
      ) : (
        isScannerActive && (
          <>
            <Webcam
              ref={webcamRef}
              onError={onError}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                facingMode: "environment",
              }}
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </>
        )
      )}
    </ContainerCam>
  )
}

export default Cam
